import React from 'react';
import { Paper, Typography, Box, Stack, CircularProgress, Button } from '@mui/material';
import {
  Login as LoginIcon,
  Logout as LogoutIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  LocalShipping as LocalShippingIcon,
  Inventory as InventoryIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { DASHBOARD_COLORS, sectionPaperSx, sectionTitleSx } from './dashboardStyles';

const TOP_LIMIT = 8;

const getActivityMeta = (log) => {
  const type = (log.subjectType || '').toUpperCase();
  const action = (log.action || '').toUpperCase();

  if (type === 'USER' && action === 'LOGIN') {
    return { label: 'Login', icon: LoginIcon, color: '#2563eb' };
  }
  if (type === 'USER' && action === 'LOGOUT') {
    return { label: 'Logout', icon: LogoutIcon, color: '#64748b' };
  }
  if (type === 'SELL' && action === 'ADDED') {
    return { label: 'Invoice created', icon: ReceiptIcon, color: '#2563eb' };
  }
  if (type === 'SELL' && action === 'EDITED') {
    return { label: 'Invoice updated', icon: ReceiptIcon, color: '#7c3aed' };
  }
  if (type === 'PURCHASE' && action === 'ADDED') {
    return { label: 'Purchase order received', icon: LocalShippingIcon, color: '#2563eb' };
  }
  if (action === 'PAYMENT' || type.includes('PAYMENT')) {
    return { label: 'Payment received', icon: PaymentIcon, color: DASHBOARD_COLORS.sales };
  }
  if (type.includes('STOCK') || type.includes('INVENTORY')) {
    return { label: 'Stock updated', icon: InventoryIcon, color: '#0d9488' };
  }
  if (log.note) {
    return { label: log.note, icon: HistoryIcon, color: DASHBOARD_COLORS.slate };
  }
  return { label: `${type} ${action}`.trim() || 'Activity', icon: HistoryIcon, color: DASHBOARD_COLORS.slate };
};

const formatActivityTime = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return format(new Date(dateStr), 'd MMM yyyy h:mm a');
  } catch {
    return '—';
  }
};

const RecentActivityFeed = ({ items, loading, onViewAll }) => {
  const rows = (items || []).slice(0, TOP_LIMIT);

  return (
    <Paper elevation={0} sx={{ ...sectionPaperSx, height: '100%', minHeight: 300, display: 'flex', flexDirection: 'column' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <HistoryIcon sx={{ fontSize: 20, color: '#7c3aed' }} />
          <Typography sx={{ ...sectionTitleSx, mb: 0 }}>Recent Activities</Typography>
        </Box>
        <Button size="small" onClick={onViewAll} sx={{ textTransform: 'none', fontWeight: 600, color: '#2563eb' }}>
          View All
        </Button>
      </Box>

      {loading ? (
        <Box flex={1} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress size={28} />
        </Box>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No recent activity.
        </Typography>
      ) : (
        <Stack spacing={0} sx={{ flex: 1 }}>
          {rows.map((log, index) => {
            const meta = getActivityMeta(log);
            const Icon = meta.icon;
            return (
              <Box
                key={log.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '110px 1fr 80px' },
                  gap: { xs: 0.5, sm: 1.5 },
                  alignItems: 'center',
                  py: 1.25,
                  borderBottom: index < rows.length - 1 ? `1px solid ${DASHBOARD_COLORS.border}` : 'none',
                }}
              >
                <Typography variant="caption" sx={{ color: DASHBOARD_COLORS.textMuted, whiteSpace: 'nowrap' }}>
                  {formatActivityTime(log.date)}
                </Typography>
                <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                  <Icon sx={{ fontSize: 18, color: meta.color, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500, color: DASHBOARD_COLORS.text }} noWrap>
                    {meta.label}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ color: DASHBOARD_COLORS.textMuted, textAlign: { xs: 'left', sm: 'right' }, fontWeight: 500 }}
                  noWrap
                >
                  {log.performedByName || 'System'}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
};

export default RecentActivityFeed;
